// Основной компонент Assistant.jsx
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { Eye, Mouth, SpeechBubble } from './AssistantComponents';
import { useAssistant } from './AssistantContext';
import { useEyeAnimation, useSleepMode, useTextTypingEffect } from './useAnimationHooks';
import styles from './Assistant.module.css';


// =================== Работающий вариант с фикс позиции ========================== //

const isValidPosition = (value) =>
  value &&
  Number.isFinite(Number(value.x)) &&
  Number.isFinite(Number(value.y));

const getDefaultAssistantPosition = () => {
  if (typeof window === 'undefined') {
    return { x: 100, y: 100 };
  }
  return { x: window.innerWidth - 120, y: window.innerHeight - 120 };
};

const AssistantInner = ({ imageSrc, isFixed = false, fixedPosition = null, isDraggable = !isFixed }) => {
  const { state, actions } = useAssistant();
  
  const [position, setPosition] = useState(
    () => (isValidPosition(fixedPosition) ? fixedPosition : getDefaultAssistantPosition())
  );
  const [isDragging, setIsDragging] = useState(false);
  const assistantRef = useRef(null);

  const eyeControls = useAnimation();
  const blinkControls = useAnimation();

  useEyeAnimation(eyeControls, blinkControls);
  const { wakeUp } = useSleepMode();
  useTextTypingEffect();

  useEffect(() => {
    if (!isValidPosition(fixedPosition)) {
      return;
    }

    setPosition((prev) => {
      if (prev.x !== fixedPosition.x || prev.y !== fixedPosition.y) {
        return fixedPosition;
      }
      return prev;
    });
  }, [fixedPosition?.x, fixedPosition?.y]);

  const handleMouseDown = useCallback(
    (e) => {
      if (!isDraggable) return;
      e.preventDefault();
      setIsDragging(true);
      actions.resetState();
      wakeUp();
    },
    [actions, wakeUp, isDraggable]
  );

  const handleMouseMove = useCallback(
    (e) => {
      if (!isDraggable || !isDragging) return;
      const newX = Math.max(0, Math.min(e.clientX - 50, window.innerWidth - 100));
      const newY = Math.max(0, Math.min(e.clientY - 50, window.innerHeight - 100));
      setPosition({ x: newX, y: newY });
      actions.updateLastInteraction();
    },
    [isDragging, actions, isDraggable]
  );

  const handleMouseUp = useCallback(() => {
    if (!isDraggable) return;
    setIsDragging(false);
    actions.updateLastInteraction();
  }, [actions, isDraggable]);

  const handleTouchStart = useCallback(
    (e) => {
      if (!isDraggable) return;
      e.preventDefault();
      setIsDragging(true);
      actions.resetState();
      wakeUp();
    },
    [actions, wakeUp, isDraggable]
  );

  const handleTouchMove = useCallback(
    (e) => {
      if (!isDraggable || !isDragging || e.touches.length !== 1) return;
      const touch = e.touches[0];
      const newX = Math.max(0, Math.min(touch.clientX - 50, window.innerWidth - 100));
      const newY = Math.max(0, Math.min(touch.clientY - 50, window.innerHeight - 100));
      setPosition({ x: newX, y: newY });
      actions.updateLastInteraction();
    },
    [isDragging, actions, isDraggable]
  );

  const handleTouchEnd = useCallback(() => {
    if (!isDraggable) return;
    setIsDragging(false);
    actions.updateLastInteraction();
  }, [actions, isDraggable]);

  useEffect(() => {
    if (!isDraggable) {
      return;
    }

    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('touchend', handleTouchEnd);
    window.addEventListener('touchmove', handleTouchMove);

    return () => {
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('touchmove', handleTouchMove);
    };
  }, [isDraggable, handleMouseUp, handleMouseMove, handleTouchEnd, handleTouchMove]);

  const getExpressionAnimation = () => {
    switch (state.expression) {
      case 'happy':
        return { y: [0, -20, 0], transition: { duration: 0.7, repeat: 1 } };
      case 'sad':
        return { rotate: [-5, 5, 0], transition: { duration: 0.7, repeat: 1 } };
      case 'surprised':
        return { scale: [1, 1.1, 1], transition: { duration: 0.6 } };
      case 'thinking':
        return { rotate: [0, 5, 0, -5, 0], transition: { duration: 1, repeat: 1 } };
      default:
        return {};
    }
  };

  const computedPosition = isValidPosition(position)
    ? position
    : (isValidPosition(fixedPosition) ? fixedPosition : getDefaultAssistantPosition());

  return (
   <>
    <motion.div
      ref={assistantRef}
      className={`${styles.assistant} ${isFixed ? styles.assistantFixed : ''}`}
      style={{ left: computedPosition.x, top: computedPosition.y, zIndex: 1000 }}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}	  
      animate={getExpressionAnimation()}
    >
      <img src={imageSrc} alt="Assistant" className={styles.image} />
      <Eye style={{ left: 20, top: 30 }} eyeControls={eyeControls} blinkControls={blinkControls} />
      <Eye style={{ right: 20, top: 30 }} eyeControls={eyeControls} blinkControls={blinkControls} />

      {state.expression !== 'default' && <Mouth expression={state.expression} />}
      {state.bubbleText && <SpeechBubble text={state.bubbleText} position={computedPosition} />}
    </motion.div>
   </>
  );
};

const Assistant = ({ imageSrc, isFixed = false, fixedPosition = null, isDraggable = !isFixed }) => {
  return (
      <AssistantInner
        imageSrc={imageSrc}
        isFixed={isFixed}
        fixedPosition={fixedPosition}
        isDraggable={isDraggable}
      />
  );
};

export default Assistant;
